import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import { XIcon, LockIcon, LogInIcon, UserCheckIcon, BookOpenIcon, AlertCircleIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useProgress } from '../hooks/useProgress';
interface LockedCourseModalProps {
  isOpen: boolean;
  onClose: () => void;
  courseTitle: string;
  courseId: string;
}
export function LockedCourseModal({
  isOpen,
  onClose,
  courseTitle,
  courseId
}: LockedCourseModalProps) {
  const {
    isAuthenticated,
    user
  } = useAuth();
  const {
    getCourseProgress
  } = useProgress();
  // Check if user is authenticated
  const needsLogin = !isAuthenticated;
  // Check if user has completed onboarding
  const hasCompletedOnboarding = user ? !!(user.fullName && user.phone && user.dateOfBirth && user.city && user.address) : false;
  // Check if user has completed Course 1
  const course1Progress = getCourseProgress('concept-clc', 6);
  const hasCompletedCourse1 = course1Progress === 100;
  // Determine what the user needs to do
  const needsOnboarding = isAuthenticated && !hasCompletedOnboarding;
  const needsCourse1 = isAuthenticated && hasCompletedOnboarding && !hasCompletedCourse1;
  // Special cases: Training 1 and 2 only need login
  const isCourse1 = courseId === 'concept-clc';
  const isCourse2 = courseId === 'proceduri-inchiriere';
  const isFirstTwoTrainings = isCourse1 || isCourse2;
  return <AnimatePresence data-id="element-1886">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" onClick={onClose} data-id="element-1887">
          <motion.div initial={{
        scale: 0.9,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} exit={{
        scale: 0.9,
        opacity: 0
      }} className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-1888">
            {/* Header */}
            <div className="bg-gradient-to-r from-[#132C53] to-[#1e4278] text-white p-6" data-id="element-1889">
              <div className="flex items-center justify-between mb-4" data-id="element-1890">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center" data-id="element-1891">
                  <LockIcon className="w-6 h-6" data-id="element-1892" />
                </div>
                <button onClick={onClose} className="text-white hover:text-gray-200 transition-colors" data-id="element-1893">
                  <XIcon className="w-6 h-6" data-id="element-1894" />
                </button>
              </div>
              <h3 className="text-2xl font-bold mb-2" data-id="element-1895">
                {isFirstTwoTrainings ? 'Necesită logare' : 'Training blocat'}
              </h3>
              <p className="text-gray-200" data-id="element-1896">{courseTitle}</p>
            </div>

            {/* Content */}
            <div className="p-6" data-id="element-1897">
              {/* Training 1 and 2 - Only need login */}
              {isFirstTwoTrainings && needsLogin && <>
                  <div className="bg-blue-50 border-l-4 border-[#357BEB] p-4 mb-6" data-id="element-1898">
                    <p className="text-sm text-[#132C53] font-medium" data-id="element-1899">
                      🎯 Pentru a accesa acest training, trebuie să te loghezi
                      sau să creezi un cont gratuit.
                    </p>
                    {isCourse2 && <p className="text-xs text-gray-600 mt-2" data-id="element-1900">
                        💡 Primele 2 training-uri sunt disponibile imediat după
                        logare.
                      </p>}
                  </div>

                  <div className="space-y-3" data-id="element-1901">
                    <Link to="/auth/register" onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-1902">
                      <LogInIcon className="w-5 h-5" data-id="element-1903" />
                      <span data-id="element-1904">Creează cont</span>
                    </Link>
                    <Link to="/auth/login" onClick={onClose} className="w-full border-2 border-[#357BEB] text-[#357BEB] px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors text-center block" data-id="element-1905">
                      Am deja cont
                    </Link>
                  </div>
                </>}

              {/* Other courses - Need login + onboarding + Course 1 */}
              {!isFirstTwoTrainings && <>
                  <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6" data-id="element-1906">
                    <div className="flex items-start space-x-2" data-id="element-1907">
                      <AlertCircleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" data-id="element-1908" />
                      <div data-id="element-1909">
                        <p className="text-sm text-amber-900 font-bold mb-1" data-id="element-1910">
                          Cerințe pentru deblocare:
                        </p>
                        <ul className="text-sm text-amber-800 space-y-1" data-id="element-1911">
                          {needsLogin && <li data-id="element-1912">• Logare în platformă</li>}
                          {needsOnboarding && <li data-id="element-1913">• Completare On-boarding Agent</li>}
                          {needsCourse1 && <li data-id="element-1914">• Finalizare Training 1</li>}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Requirements breakdown */}
                  <div className="space-y-4 mb-6" data-id="element-1915">
                    <h4 className="font-bold text-[#132C53] mb-3" data-id="element-1916">
                      Ce trebuie să faci:
                    </h4>

                    {/* Step 1: Login */}
                    <div className="flex items-start space-x-3" data-id="element-1917">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${needsLogin ? 'bg-gradient-to-br from-[#357BEB] to-[#2868d8]' : 'bg-emerald-500'}`} data-id="element-1918">
                        {needsLogin ? <LogInIcon className="w-5 h-5 text-white" data-id="element-1919" /> : <UserCheckIcon className="w-5 h-5 text-white" data-id="element-1920" />}
                      </div>
                      <div className="flex-1" data-id="element-1921">
                        <h5 className="font-semibold text-[#132C53] mb-1" data-id="element-1922">
                          1. Logare / Creare cont
                          {!needsLogin && <span className="ml-2 text-emerald-600 text-sm" data-id="element-1923">
                              ✓ Completat
                            </span>}
                        </h5>
                        <p className="text-sm text-gray-600" data-id="element-1924">
                          {needsLogin ? 'Creează un cont sau loghează-te în platformă' : 'Ești autentificat în platformă'}
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Onboarding */}
                    <div className="flex items-start space-x-3" data-id="element-1925">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${needsOnboarding || needsLogin ? 'bg-gray-300' : 'bg-emerald-500'}`} data-id="element-1926">
                        <UserCheckIcon className="w-5 h-5 text-white" data-id="element-1927" />
                      </div>
                      <div className="flex-1" data-id="element-1928">
                        <h5 className="font-semibold text-[#132C53] mb-1" data-id="element-1929">
                          2. Completare On-boarding Agent
                          {!needsOnboarding && !needsLogin && <span className="ml-2 text-emerald-600 text-sm" data-id="element-1930">
                              ✓ Completat
                            </span>}
                        </h5>
                        <p className="text-sm text-gray-600" data-id="element-1931">
                          {needsLogin ? 'Disponibil după logare' : needsOnboarding ? 'Completează procesul de on-boarding' : 'On-boarding finalizat cu succes'}
                        </p>
                      </div>
                    </div>

                    {/* Step 3: Course 1 */}
                    <div className="flex items-start space-x-3" data-id="element-1932">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${needsCourse1 || needsOnboarding || needsLogin ? 'bg-gray-300' : 'bg-emerald-500'}`} data-id="element-1933">
                        <BookOpenIcon className="w-5 h-5 text-white" data-id="element-1934" />
                      </div>
                      <div className="flex-1" data-id="element-1935">
                        <h5 className="font-semibold text-[#132C53] mb-1" data-id="element-1936">
                          3. Finalizare Training 1
                          {!needsCourse1 && !needsOnboarding && !needsLogin && <span className="ml-2 text-emerald-600 text-sm" data-id="element-1937">
                              ✓ Completat
                            </span>}
                        </h5>
                        <p className="text-sm text-gray-600" data-id="element-1938">
                          {needsLogin || needsOnboarding ? 'Disponibil după on-boarding' : needsCourse1 ? 'Finalizează Training 1 — Concept CLC' : 'Training 1 finalizat cu succes'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* CTA Buttons */}
                  <div className="space-y-3" data-id="element-1939">
                    {needsLogin ? <>
                        <Link to="/auth/register" onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-1940">
                          <LogInIcon className="w-5 h-5" data-id="element-1941" />
                          <span data-id="element-1942">Creează cont</span>
                        </Link>
                        <Link to="/auth/login" onClick={onClose} className="w-full border-2 border-[#357BEB] text-[#357BEB] px-6 py-3 rounded-xl font-semibold hover:bg-blue-50 transition-colors text-center block" data-id="element-1943">
                          Am deja cont
                        </Link>
                      </> : needsOnboarding ? <>
                        <Link to="/onboarding" onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-1944">
                          <UserCheckIcon className="w-5 h-5" data-id="element-1945" />
                          <span data-id="element-1946">Completează On-boarding</span>
                        </Link>
                        <button onClick={onClose} className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-1947">
                          Înapoi la cursuri
                        </button>
                      </> : needsCourse1 ? <>
                        <Link to="/cursuri/concept-clc" onClick={onClose} className="w-full bg-gradient-to-r from-[#357BEB] to-[#2868d8] hover:from-[#2868d8] hover:to-[#357BEB] text-white px-6 py-4 rounded-xl font-bold transition-all shadow-lg flex items-center justify-center space-x-2" data-id="element-1948">
                          <BookOpenIcon className="w-5 h-5" data-id="element-1949" />
                          <span data-id="element-1950">Mergi la Training 1</span>
                        </Link>
                        <button onClick={onClose} className="w-full border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-xl font-semibold hover:bg-gray-50 transition-colors" data-id="element-1951">
                          Înapoi la training-uri
                        </button>
                      </> : null}
                  </div>
                </>}

              {/* Info note */}
              <div className="mt-6 pt-6 border-t border-gray-200" data-id="element-1952">
                <p className="text-xs text-gray-500 text-center" data-id="element-1953">
                  💡 Training-urile se deblochează automat după îndeplinirea
                  cerințelor. Progresul tău este salvat automat.
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}